import { useCallback, useEffect } from "react";
import api from "../../services/api";
import UserPermission from "../../components/UserPermission";
import moment from "moment";
import { useSearch } from "./useSearch";

const userPermission = UserPermission();

function instanteFinal(date) {
   const newDate = new Date(date);
   newDate.setHours(23, 59, 59, 999);
   return newDate;
}

export function useSearchFilter() {
   const {
      limit,
      offset, setOffset, setSearchId,
      setConfirmDialog, setComplaints,
      listAreas, status, area,
      complaintTarget, search,
      estadoOcorrencia, bairro,
      late, group, setIsLoading,
      setTotal, rangeOccurrences,
      stepStatus, executor, controlador
   } = useSearch();

   const buildFilter = useCallback(({ offset, limit, status, area, complaintTarget, search, estadoOcorrencia, bairro, late, group, rangeOccurrences, stepStatus, executor, controlador }) => {
      let filter = {
         order: "createdAt DESC",
         where: {
            createdAt: {
               between: [
                  moment(rangeOccurrences.startDate).toISOString(),
                  moment(instanteFinal(rangeOccurrences.endDate)).toISOString()
               ]
            }
         },
         limit: limit,
         offset: offset,
         fields: ['id', 'area', 'group', 'createdAt', 'status', 'neighborhood', 'reference', 'datePrevision', 'validationControl', 'statusStep', 'executorId', 'executorName']
      };

      if (status !== "Todos") {
         filter.where.status = status;
      }

      if (area !== "Todos") {
         filter.where.area = area;
      }

      if (complaintTarget !== "Todos") {
         filter.where.complaintTarget = complaintTarget;
      }

      if (stepStatus !== "Todos") {
         // Mapeamento de Etapa dos Status para o campo status
         const stepStatusMapping = {
            "Abertura": ["Novo"],
            "Homologação": ["Em análise", "Aguardando Informação", "Autorizado/Aprovado", "Solução de longo prazo / Sugestão de projeto"],
            "Execução": ["Em execução", "Encaminhado para o executor", "Notificado para providências"],
            "Conclusão": ["Resolvido", "Indeferido", "Respondido", "Detecção incorreta"]
         };

         const mappedStatus = stepStatusMapping[stepStatus];
         filter.where.status = { inq: mappedStatus };
      }

      if (search !== '') {
         filter.where.or = [
            { group: { like: `.*${search}.*` } },
            { area: { like: `.*${search}.*` } },
            { neighborhood: { like: `.*${search}.*` } },
            { description: { like: `.*${search}.*` } },
            { status: { like: `.*${search}.*` } },
            { reference: { like: `.*${search}.*` } },
            { address: { like: `.*${search}.*` } }
         ];

      }

      if (estadoOcorrencia !== "Todos") {
         filter.where.validationControl = estadoOcorrencia;
      }

      if (group !== "Todos") {
         filter.where.group = group;
      }

      if (bairro?.name !== "Todos") {
         filter.where.neighborhood = bairro?.name;
      }

      if (late !== "Todos") {
         filter.where.datePrevision = late === "true"
            ? { lt: moment().startOf('day').toISOString() }
            : { gte: moment().startOf('day').toISOString() };
      }

      if (executor !== "Todos") {
         filter.where.executorName = executor;
      }

      if (controlador !== "Todos") {
         filter.where.controladorName = controlador;
      }

      return filter;
   }, []);

   const handleOpenNotFoundComplaint = useCallback(() => {
      setConfirmDialog({
         isOpen: true,
         subTitle: `Não foi possível encontrar nenhuma ocorrência.`,
         onConfirm: () => { window.location.reload() }
      });
   }, []);

   const updateOccurences = useCallback(async (data) => {
      const filter = buildFilter(data);

      setIsLoading(true);

      const { data: count } = await api.get(`${userPermission}/complaints/count?where=${JSON.stringify(filter.where)}`);
      const { data: complaints } = await api.get(`${userPermission}/complaints?filter=${JSON.stringify(filter)}`);

      /* Resolve o nome do executor pelo executorId quando o campo executorName não está preenchido na ocorrência */
      const executorIds = [...new Set(complaints.filter(c => !c.executorName && c.executorId).map(c => c.executorId))];
      if (executorIds.length > 0 && userPermission !== 'executor') {
         try {
            const executorFilter = { where: { id: { inq: executorIds } } };
            const { data: executors } = await api.get(`${userPermission}/users?filter=${JSON.stringify(executorFilter)}`);
            complaints.forEach(complaint => {
               if (!complaint.executorName && complaint.executorId) {
                  const executor = executors.find(user => user.id === complaint.executorId);
                  if (executor) complaint.executorName = executor.name;
               }
            });
         } catch (error) {
            console.log(error);
         }
      }

      //Carousel - Marcus
      //É feito um GET apenas para o carrosel para que o limite do array seja o proprio tamanho dele.
      let newLimit;
      count.count === 0 ? newLimit = 1 : newLimit = count.count;
      const carouselFilter = { ...filter, limit: newLimit };
      const { data: complaintsCarousel } = await api.get(`${userPermission}/complaints?filter=${JSON.stringify(carouselFilter)}`);
      if (complaintsCarousel) {
         sessionStorage.setItem("idComplaintsFiltered", JSON.stringify(complaintsCarousel.map(data => data.id)));
      }
      //Ate aqui

      setIsLoading(false);

      if (complaints.length === 0 && search !== '')
         handleOpenNotFoundComplaint();

      setTotal(count.count);
      setComplaints(complaints);
      setSearchId('');
   }, []);

   //Carousel - Marcus
   //Passamos os dados para o GlobalStorage
   //Passamos apenas o ID das ocorrencias
   //Ate aqui

   useEffect(() => {
      updateOccurences({
         userPermission,
         offset,
         limit,
         status,
         area,
         complaintTarget,
         search,
         estadoOcorrencia,
         bairro,
         late,
         group,
         rangeOccurrences,
         stepStatus,
         executor,
         controlador
      });
   }, [limit, offset])

   useEffect(() => {
      //Depois disso ele tem que zerar o offset e chamar a função
      if (offset !== 0)
         setOffset(0); //Se for diferente de zero, ele zera o offset e atualiza
      else //Caso contrario somente atualiza
         updateOccurences({
            userPermission,
            offset,
            limit,
            status,
            area,
            complaintTarget,
            search,
            estadoOcorrencia,
            bairro,
            late,
            group,
            rangeOccurrences,
            stepStatus,
            executor,
            controlador
         });

   }, [userPermission, listAreas, status, area, complaintTarget, search, estadoOcorrencia, bairro, late, group, rangeOccurrences, stepStatus, executor, controlador]);
}